import { useStore } from "@nanostores/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import {
  dataStore,
  filteredValuesStore,
  fixedValuesStore,
  getSearchBasePath,
  selectedValueStore,
  setTypeValues,
  updateUrl,
} from "../../_utils/searchStore";
import type { Value } from "../../_utils/types";
import { Checkbox } from "./Checkbox";
import { Collapse } from "./Collapse";
import { MultiSelect } from "./MultiSelect";
import { drawerTypeStore } from "./store";

type Props = {
  isDesktop?: boolean;
  fit?: boolean;
};
export const Destinations = ({ isDesktop = false, fit = false }: Props) => {
  const router = useRouter();
  const state = useStore(drawerTypeStore);
  const selectedDestinations = useStore(filteredValuesStore("destinations"));
  const [localDestinations, setLocalDestinations] = useState(selectedDestinations);
  const selectedDestinationsRef = useRef(selectedDestinations);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldT = selectedDestinationsRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newT = selectedDestinations.sort((a, b) => a.value.localeCompare(b.value));

      if (oldT.length !== newT.length) return true;

      for (let index = 0; index < newT.length; index++) {
        if (oldT[index] !== newT[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedDestinationsRef.current = selectedDestinations;
      setLocalDestinations(selectedDestinations);
    }
  }, [selectedDestinations]);

  const handleChange = (newValue: Value, directUpdate = false) => {
    let newDestinations: Array<Value> = [];
    if (localDestinations.some((lt) => lt.value === newValue.value)) {
      newDestinations = localDestinations.filter((lt) => lt.value !== newValue.value);
    } else {
      newDestinations = [...localDestinations, newValue];
    }

    setLocalDestinations(newDestinations);

    if (directUpdate) {
      if (
        fixedValuesStore
          .get()
          .flat()
          .some((lt) => lt.value === newValue.value)
      ) {
        router.push(getSearchBasePath());
        return;
      }

      if (selectedValueStore.get()?.value === newValue.value) {
        selectedValueStore.set(null);
      }

      setTypeValues("destinations", newDestinations);
      updateUrl(router);
    }
  };

  const handleOpen = () => {
    drawerTypeStore.set(state === "destinations" ? null : "destinations");
  };

  const handleClickOutside = () => {
    setTypeValues("destinations", localDestinations);

    drawerTypeStore.set(null);
  };

  const handleValidate = () => {
    setTypeValues("destinations", localDestinations);
    updateUrl(router);
  };

  const handleClear = () => {
    setTypeValues("destinations", []);
    updateUrl(router);
  };

  const destinations = [
    ...selectedDestinations
      .filter((s) => !data?.destinations.some((t) => t.key === s.value))
      .map((s) => ({
        value: s.value,
      })),
    ...(data?.destinations.map((t) => ({ value: t.key })) || []),
  ];

  return isDesktop ? (
    <MultiSelect
      fit={fit}
      placeholder="Destinations"
      type="destinations"
      onChange={handleChange}
      onValidate={handleValidate}
      onClear={handleClear}
      localValues={localDestinations}
      selectedValues={selectedDestinations}
      values={destinations.map((d) => ({ label: d.value, value: d.value }))}
      large
    />
  ) : (
    <Collapse
      open={state === "destinations"}
      title="Régions"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedDestinations.length}
    >
      <div className="mt-5 grid sm:grid-cols-2 gap-[10px]">
        {destinations
          .sort((a, b) => a.value.localeCompare(b.value))
          .map((destination) => (
            <Checkbox
              key={destination.value}
              label={destination.value}
              checked={localDestinations.some((lt) => lt.value === destination.value)}
              onChange={() =>
                handleChange({
                  type: "destinations",
                  value: destination.value,
                })
              }
            />
          ))}
      </div>
    </Collapse>
  );
};
